import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Hook to detect user idle state and trigger warnings/timeouts.
 *
 * Tracks activity via DOM events (click, keydown, touchstart, mousemove)
 * and can be manually reset when socket events indicate activity.
 *
 * @param {Object} options Configuration options
 * @param {number} options.timeoutMs - Time before warning (default: 10 minutes)
 * @param {number} options.warningDurationMs - Time before auto-disconnect after warning (default: 2 minutes)
 * @param {boolean} options.enabled - Whether idle tracking is enabled (default: true)
 * @returns {Object} Idle state and control functions
 */
export function useIdleTimeout({
  timeoutMs = 10 * 60 * 1000,  // 10 minutes
  warningDurationMs = 2 * 60 * 1000,  // 2 minutes
  enabled = true,
} = {}) {
  const [isWarning, setIsWarning] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [warningSecondsLeft, setWarningSecondsLeft] = useState(0);

  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isWarningRef = useRef(isWarning);

  useEffect(() => {
    isWarningRef.current = isWarning;
  }, [isWarning]);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Start the warning countdown
  const startWarningCountdown = useCallback(() => {
    setIsWarning(true);
    setWarningSecondsLeft(Math.ceil(warningDurationMs / 1000));

    // Update countdown every second
    countdownIntervalRef.current = setInterval(() => {
      setWarningSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Set timer for when warning expires
    warningTimerRef.current = setTimeout(() => {
      console.log('⏰ [IDLE] Warning expired - timing out');
      setIsWarning(false);
      setIsTimedOut(true);
      clearAllTimers();
    }, warningDurationMs);
  }, [warningDurationMs, clearAllTimers]);

  // Reset the idle timer (call on activity)
  const resetTimer = useCallback((activityType = 'implicit') => {
    if (!enabled) return;
    if (isWarningRef.current && activityType !== 'explicit') return;

    lastActivityRef.current = Date.now();

    // If we're already timed out, don't reset (user needs to click reconnect)
    if (isTimedOut) return;

    // Clear existing timers
    clearAllTimers();
    setIsWarning(false);
    setWarningSecondsLeft(0);

    // Start new idle timer
    idleTimerRef.current = setTimeout(() => {
      console.log('⏰ [IDLE] Idle timeout reached - showing warning');
      startWarningCountdown();
    }, timeoutMs);
  }, [enabled, isTimedOut, timeoutMs, clearAllTimers, startWarningCountdown]);

  // User acknowledged the warning ("I'm here" button)
  const acknowledgeWarning = useCallback(() => {
    console.log('⏰ [IDLE] User acknowledged - resetting timer');
    setIsWarning(false);
    setWarningSecondsLeft(0);
    clearAllTimers();

    // Restart the idle timer
    idleTimerRef.current = setTimeout(() => {
      console.log('⏰ [IDLE] Idle timeout reached - showing warning');
      startWarningCountdown();
    }, timeoutMs);
  }, [timeoutMs, clearAllTimers, startWarningCountdown]);

  // User chose to disconnect voluntarily
  const voluntaryDisconnect = useCallback(() => {
    console.log('⏰ [IDLE] User chose to disconnect');
    setIsWarning(false);
    setIsTimedOut(true);
    clearAllTimers();
  }, [clearAllTimers]);

  // User wants to reconnect after timeout
  const reconnect = useCallback(() => {
    console.log('⏰ [IDLE] User reconnecting');
    setIsTimedOut(false);
    setIsWarning(false);
    // Don't reset timer here - let the caller handle reconnection
    // Timer will be reset when they call resetTimer() after reconnecting
  }, []);

  // Set up DOM event listeners for activity tracking
  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      return;
    }

    const activityEvents = ['click', 'keydown', 'touchstart', 'mousemove'];

    const handleActivity = (event) => {
      // Throttle mousemove to avoid excessive resets
      const now = Date.now();
      if (now - lastActivityRef.current < 1000) return;

      const isExplicit = event.type !== 'mousemove';
      resetTimer(isExplicit ? 'explicit' : 'implicit');
    };

    // Add listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Arm the timer for this configuration. Deliberately 'explicit': this effect
    // re-runs whenever `enabled` or `timeoutMs` changes, and its cleanup has just
    // cleared the live timers. A plain reset would be swallowed by the
    // warning-in-progress guard in resetTimer, leaving the modal on screen with
    // nothing armed behind it — the guard silently dead for the rest of the session
    // (BOU-2974). A configuration change legitimately restarts the countdown; only
    // passive mousemove is meant to be ignored mid-warning.
    resetTimer('explicit');

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
  }, [enabled, resetTimer, clearAllTimers]);

  return {
    // State
    isWarning,
    isTimedOut,
    warningSecondsLeft,

    // Actions
    resetTimer,           // Call when socket events indicate activity
    acknowledgeWarning,   // Call when user clicks "I'm here"
    voluntaryDisconnect,  // Call when user clicks "Disconnect"
    reconnect,            // Call when user clicks "Reconnect"
  };
}

export default useIdleTimeout;
